import Joi from 'joi'
import { v4 as uuidv4 } from 'uuid'
import prettyBytes from 'pretty-bytes'
import * as queryString from 'query-string'
import { useLocation } from 'react-router-dom'
import { isValidPhoneNumber } from 'libphonenumber-js'
import i18n from './i18n'
import { formatVienna } from './datetime'

import http from './http'
import store from '../redux'
import { clearStoredFilters } from '../UI/FilterPanel/useFilters'
import { logIn, logOut } from '../redux/ducks/user'
import { addOptions, resetPrivateOptions } from '../redux/ducks/options'

import {
	TCompanies,
	DocumentOptions,
	StateOptions,
	StateShortLabels,
	RoleLabels,
	TicketStatusOptions,
	TicketTypeOptions,
	TicketUrgencyOptions,
	TicketImportanceOptions,
	ChangelogKindOptions,
	ChangelogEnvOptions,
	ChangelogRepoOptions,
	TApi
} from './const'

import WarningIcon from '../assets/icons/danger.svg'
import CheckIcon from '../assets/icons/checkGreen.svg'
import { sollVerkleinern } from './shrinkImage'

/**
 * Vor- und Nachname: Buchstaben jeder Schrift, Wortteile getrennt durch
 * Leerzeichen, Bindestrich oder Apostroph - also auch "Bauer-Müller",
 * "Anne Marie", "O'Brien". Die alte Regel liess nur Buchstaben und
 * Leerzeichen zu; Doppelnamen liessen sich weder eintragen noch ein Profil
 * mit einem solchen Namen speichern (Anforderung #201).
 * Der Fehlertext steht als `string.pattern.name` in shared/i18n.
 */
export const NAME_PATTERN = /^[\p{L}\p{M}]+(?:[ '’-][\p{L}\p{M}]+)*$/u

/**
 * Geburtsdatum als reines Kalenderdatum "JJJJ-MM-TT".
 *
 * Ein Date-Objekt ging als Mitternacht Wiener Zeit hinaus, also als 22:00 bzw.
 * 23:00 UTC des Vortags, und die Datenbank speicherte den Vortag (Anforderung
 * #203). Gelesen wird ebenso ohne Umweg ueber Date: `new Date("1979-05-04")`
 * ist Mitternacht UTC und je nach Zeitzone des Rechners schon der 3. Mai.
 */
export const toDateOnly = (year, month, day) =>
	`${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`

export const splitDateOnly = value => {
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(typeof value === 'string' ? value : '')
	if (!match) return { day: undefined, month: undefined, year: undefined }
	return { year: String(Number(match[1])), month: String(Number(match[2])), day: String(Number(match[3])) }
}

export const megabytesToBytes = megabytes => {
	return megabytes * 1048576 // 1 Mb = 1048576 bytes
}

export const joiPhone = Joi.string()
	.custom((value, helper) => {
		if (isValidPhoneNumber(value)) return value

		return helper.error('phoneNumber.invalid')
	})
	.required()

export const joiFile = maxMegabytes =>
	Joi.custom((value, helper) => {
		if (!value[0]) return value[0]
		// Fotos werden vor dem Hochladen verkleinert (shared/shrinkImage) — die
		// Grenze gilt für das, was ankommt; der Server misst noch einmal.
		if (sollVerkleinern(value[0])) return value
		if (megabytesToBytes(maxMegabytes) < value[0].size)
			return helper.message(
				`${i18n.t('The file is too large. Current file size')}: ${prettyBytes(value[0].size)}. ${i18n.t(
					'Max file size is'
				)} ${maxMegabytes}M.`
			)
		return value
	})

/*
 * Die Expert:innen-Ansicht merkt sich, fuer welche Junior Company sie liest
 * (`views/common/SupportersList`). Auch das haengt am Browser, nicht am Konto —
 * und anders als ein Filter geht es an den Server: Wer sich am selben Rechner
 * als andere Person anmeldet, schickte die Firma der Vorgaengerin mit und
 * bekam zu Recht 403 zurueck. Deshalb wird die Wahl bei jedem Wechsel der
 * Anmeldung verworfen.
 */
export const SUPPORTERS_COMPANY_STORAGE_KEY = 'supporters.companyId'

/** Was sich die Listen im Browser gemerkt haben — beim Wechsel der Anmeldung. */
const clearStoredListState = () => {
	clearStoredFilters()
	localStorage.removeItem(SUPPORTERS_COMPANY_STORAGE_KEY)
}

export const fullLogOut = () => {
	localStorage.removeItem('auth')
	// Die Filterleiste merkt sich ihren Stand im Browser, nicht am Konto. Ohne
	// dieses Aufraeumen saehe die naechste Anmeldung am selben Rechner die
	// Listen der Vorgaengerin gefiltert.
	clearStoredListState()
	store.dispatch(logOut())
	// Was nur angemeldet ausgeliefert wird, bleibt nicht für die Anmeldeseite
	// (oder das nächste Konto am selben Rechner) im Store liegen.
	store.dispatch(resetPrivateOptions())
	delete http.defaults.headers['Authorization']
}

export const fullLogIn = (token, user) => {
	http.defaults.headers['Authorization'] = `Bearer ${token}`
	// Auch hier, nicht nur beim Abmelden: Eine neue Anmeldung loest die vorige
	// ab, ohne dass jemand abgemeldet haette (zweites Konto, abgelaufene
	// Sitzung, Anmeldung ueber einen Link).
	clearStoredListState()
	store.dispatch(
		logIn({
			role: user.role,
			profile: user
		})
	)
	localStorage.setItem(
		'auth',
		JSON.stringify({
			role: user.role,
			token: token
		})
	)
	loadPrivateOptions()
	refreshProfileAfterLogIn(token)
}

/*
 * Die Einstellungen, die nur angemeldet ausgeliefert werden (je Rolle, Backend:
 * services/options/audience.js). Ohne Anmeldung liefert
 * `GET /api/public/options` seit 14.10.2026 nur noch die Auswahllisten der
 * Registrierung; Berichtsfristen, Checkliste, Steuersätze usw. kommen von hier
 * und werden in denselben Zweig `state.options` gemischt.
 *
 * Kennt das Backend die Route noch nicht (älterer Stand), bleibt es still beim
 * öffentlichen Stand — der war dort ohnehin vollständig. Hat sich inzwischen
 * jemand anderes an- oder abgemeldet, bleibt die Antwort liegen. Antwortet der
 * Webserver mit etwas anderem als einem Objekt (die Startseite als HTML, siehe
 * „Abfrage ohne Rolle"), wird nichts übernommen.
 *
 * Gibt die geladenen Werte zurück, sonst `null`.
 */
export const loadPrivateOptions = () => {
	const header = http.defaults.headers['Authorization']
	if (!header) return Promise.resolve(null)

	return http
		.get(TApi.COMMON_GET_PRIVATE_OPTIONS, { disableToast: true })
		.then(({ data }) => {
			if (http.defaults.headers['Authorization'] !== header) return null
			if (!data || typeof data !== 'object' || Array.isArray(data)) return null
			store.dispatch(addOptions(data))
			return data
		})
		.catch(() => null)
}

/*
 * Die Antwort der Anmeldung traegt nur den Datensatz des Kontos. Was `/me`
 * dazurechnet — welche Anliegen das Hilfe-Formular anbietet (`requestTopics`,
 * Anforderung #216), ob der Hilfe-Knopf etwas bewirkt (`mayCreateRequest`) —
 * fehlte deshalb bis zum naechsten Neuladen: Wer sich frisch anmeldete, sah
 * „Skills4Retail" nicht. Nach jeder Anmeldung wird das Profil deshalb einmal
 * nachgeladen. Hat sich inzwischen jemand anderes angemeldet oder ab, bleibt
 * die Antwort liegen.
 */
export const refreshProfileAfterLogIn = token =>
	http
		.get(TApi.COMMON_ME, { disableToast: true })
		.then(({ data }) => {
			if (http.defaults.headers['Authorization'] !== `Bearer ${token}`) return
			store.dispatch(logIn({ role: data.role, profile: data }))
		})
		.catch(() => {})

export const convertValuesToOptions = (values, options) => {
	const valuesLabels = options.reduce((acc, curr) => {
		acc[curr.value] = curr.label
		return acc
	}, {})

	return values.map(value => ({
		value,
		label: valuesLabels[value]
	}))
}
/*
 * Rechnet in Wiener Zeit (siehe shared/datetime.js).
 *
 * Die Schreibweise mit Schraegstrichen (24/12/2026) ist Bestand — sie kam von
 * 'en-GB' und bleibt hier unveraendert, damit die Zeitzonen-Umstellung nicht
 * nebenbei das Aussehen von vier Listen aendert.
 */
export const getFormattedDate = value => formatVienna(value, 'dd/MM/yyyy')

/*
 * Restlaufzeit einer Einladung. Mitarbeiter:innen-Einladungen gelten fest 60
 * Tage; bei Unterstützer:innen ist die Frist einstellbar (14–90) und kommt als
 * `validDays` mit jeder Einladung vom Server.
 */
export const invitationValidity = (invitedAt, validDays = 60) => {
	if (!invitedAt) return null
	const end = new Date(invitedAt).getTime() + (Number(validDays) || 60) * 24 * 60 * 60 * 1000
	const daysLeft = Math.ceil((end - Date.now()) / (24 * 60 * 60 * 1000))
	return { expired: daysLeft <= 0, daysLeft }
}

/*
 * Ampel einer offenen Einladung (Spalte „Eingeladen am“ der Unterstützer:innen):
 * grün mit mehr als drei Wochen Rest, gelb ab drei Wochen, rot in der letzten
 * Woche und danach. Die 60 Tage setzt der Server seit 31.10.2026 auch durch.
 */
export const INVITATION_TONE_YELLOW_DAYS = 21
export const INVITATION_TONE_RED_DAYS = 7

export const invitationTone = (invitedAt, validDays) => {
	const validity = invitationValidity(invitedAt, validDays)
	if (!validity) return null
	if (validity.expired) return 'expired'
	if (validity.daysLeft <= INVITATION_TONE_RED_DAYS) return 'red'
	if (validity.daysLeft <= INVITATION_TONE_YELLOW_DAYS) return 'yellow'
	return 'green'
}

export const useInvite = () => {
	const { search } = useLocation()
	const { invite, role, email } = queryString.parse(search)

	return {
		invite,
		role,
		email,
		isToken: !!invite
	}
}

export const mapCompanyTypeToName = type => {
	const names = {
		[TCompanies.JCC]: 'Junior Compact Company',
		[TCompanies.JC]: 'Junior Company',
		[TCompanies.JBC]: 'Junior Basic Company'
	}

	return names[type]
}

/*
 * Die betreuende Lehrkraft einer Junior Company. In `TeachersSupervisions`
 * stehen auch die ueber „Lehrer hinzufuegen“ ergaenzten Lehrkraefte, und die
 * Schnittstelle liefert die Liste ohne feste Reihenfolge — der erste Eintrag
 * ist also nicht verlaesslich der oder die Betreuende. Faellt auf den ersten
 * Eintrag zurueck, falls (bei Altbestand) keiner den Vermerk traegt.
 */
export const companySupervision = company =>
	company?.TeachersSupervisions?.find(({ isSupervision }) => isSupervision) ||
	company?.TeachersSupervisions?.[0] ||
	null

// Vor- und Nachname einer Person, ohne "undefined undefined" bei fehlenden Daten.
export const personName = person => [person?.firstName, person?.lastName].filter(Boolean).join(' ')

/**
 * Zu wem gehört dieses Dokument? (Anforderung #94)
 *
 * Die Dokumentenlisten zeigen bisher Firma, Art, Datum und Dateiname — bei
 * Einverständniserklärungen und Kursnachweisen ist damit nicht zu erkennen,
 * um welche Schüler:in es geht; sie heißen alle „Einverständniserklärung.pdf".
 *
 * Zuordenbar ist ein Dokument über `Creator`. Erzeugte Unterlagen — Urkunden,
 * Rechnungen, Firmenzertifikate — haben niemanden; dann liefert die Funktion
 * einen leeren Text, und die Spalte bleibt in dieser Zeile leer.
 *
 * **Nicht zu verwechseln mit „wer hat hochgeladen":** Lädt eine Lehrkraft die
 * Einverständniserklärung an Stelle der Schüler:in hoch, bleibt die Schüler:in
 * die zugehörige Person. Wer hochgeladen hat, steht im Verlauf der Junior
 * Company.
 */
export const documentOwnerName = creator =>
	creator ? [creator.firstName, creator.lastName].filter(Boolean).join(' ') : ''

// Label fuer einen Dokumenttyp. COURSE_CERTIFICATE zeigte bis 31.10.2026 den
// Namen aus der Einstellung `courseCertificateRequirement`; seit der
// Kursnachweis ausgebaut ist, heissen die verbliebenen Dateien schlicht
// "Kursnachweis".
export const localizedDocumentTypeLabel = type => {
	const option = DocumentOptions.find(({ value }) => value === type)
	return i18n.t(option?.label)
}

export const getRandomUUID = () => uuidv4()
export const downloadFile = (name, data) => {
	const url = window.URL.createObjectURL(new Blob([data], { type: data.type }))
	const link = document.createElement('a')
	link.href = url
	link.setAttribute('download', name)
	document.body.appendChild(link)
	link.click()
	link.parentNode.removeChild(link)
}

/**
 * Dateiname für eine erzeugte Ausgabe (XLS/PDF).
 *
 * Der vorgeschlagene Name ist das Einzige, was im Speichern-Fenster steht —
 * „Company" für jede Company zwingt dazu, jede Datei von Hand umzubenennen.
 * Deshalb steht der Name des Datensatzes darin. Zeichen, die Windows oder
 * macOS im Dateinamen nicht zulassen, werden ersetzt; ohne Endung würde die
 * Datei am Rechner nicht mit dem Tabellenprogramm geöffnet.
 */
export const exportFileName = (name, extension = 'xlsx') => {
	const clean = String(name || '')
		.replace(/[\\/:*?"<>|]/g, '-')
		.replace(/\s+/g, ' ')
		.trim()

	return `${clean || 'Export'}.${extension}`
}

export const customNumberFormat = number => {
	if (!number) return 0
	return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2 }).format(number)
}

export const blobTypes = {
	XLS: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;',
	PDF: 'application/pdf'
}

export const customNumberFormatW = number => {
	if (!number) return 0
	return new Intl.NumberFormat('de-DE').format(number)
}

/*
 * Die vier Geldspalten der Berichtsliste stehen je nach Unternehmensart in
 * unterschiedlichen Feldern des Berichts. Wer diese Zuordnung ändert, muss sie
 * im Backend mitziehen (`src/utils/reportOrder.js`) — sonst sortiert die Liste
 * nach einem anderen Feld, als sie anzeigt.
 */
export const reportData = (type, data) => {
	switch (type) {
		case TCompanies.JC:
			return {
				revenues: data.advanceVatReturnTotalIncome,
				expenses: data.advanceVatReturnTotalExpenses,
				result: data.semiAnnualReportResultTotal,
				charges: data.semiAnnualReportTotalDuties
			}
		case TCompanies.JCC:
			return {
				revenues: data.totalGrossRevenue,
				expenses: data.totalExpenses,
				result: data.profitLossReducedCorporateIncomeTax,
				charges: data.taxesTransferredToJuniorAustria
			}
		case TCompanies.JBC:
			return {
				revenues: data.totalGrossRevenue,
				expenses: data.totalExpenses,
				result: data.netProfitLoss,
				charges: data.insurance.JBC
			}
		default:
			return {}
	}
}

/*
 * Der Weg dorthin, wo eine Voraussetzung zu erfüllen ist — „Zum Kurs“, „Zu den
 * Mitarbeiter:innen“, … Steht hier und nicht in der Liste selbst, damit die
 * Voraussetzungen reine Daten bleiben und auch dort verwendbar sind, wo React
 * rendert statt sweetalert2.
 *
 * Bis 21.08.2026 war das ein dunkler Knopf hinter der Überschrift. Er zog in
 * einer Liste aus neun Punkten neunmal den Blick auf sich — gesucht wird aber
 * der eine Punkt, der noch fehlt. Deshalb jetzt ein ruhiger Verweis am Ende der
 * Karte, in derselben Gestalt wie in der Checkliste des Dashboards
 * (views/common/CompanySteps).
 *
 * Ein Verweis innerhalb der Anwendung öffnet kein zweites Fenster: Der Dialog
 * liegt über der Seite, die man ohnehin verlässt, und ein zweiter Reiter mit
 * derselben Anwendung wäre eine zweite Anmeldung im Kopf der Nutzer:in.
 */
const requirementLink = (href, label) => {
	if (!href) return ''

	const isInternal = href.startsWith('/')
	const target = isInternal ? '_self' : '_blank'

	return `<a class="approval-link" href="${href}" target="${target}" rel="noopener noreferrer">${
		label || i18n.t('To the course')
	}<span class="approval-link-arrow" aria-hidden="true">\u2192</span></a>`
}

/*
 * Die Voraussetzungen als Fensterinhalt (sweetalert2 nimmt nur HTML entgegen).
 *
 * Gestalt wie die Checkliste auf dem Dashboard: weiße Karten auf hellem Grund,
 * dunkelblaue Überschrift, grauer Erklärsatz, ein ruhiger Verweis am Ende.
 * Vorher waren die Karten flächig eingefärbt — Grün und Orange über die
 * ganze Zeile —, und dieselbe Liste sah an zwei Stellen verschieden aus.
 *
 * Der Balken oben nennt dieselbe Zahl wie das Dashboard („6 von 8 erledigt“).
 * Ohne ihn beantwortet das Fenster nur „was fehlt“, nicht „wie weit sind wir“.
 */
export const renderRequirements = (items, isAllRequirements = false) => {
	const done = items.filter(item => item.status === true).length
	const percent = items.length ? Math.round((done / items.length) * 100) : 0

	return `
	<div class="approval-box">
		<div class="icon-wrapper ${isAllRequirements ? 'check' : 'warn'}">
			<img src="${isAllRequirements ? CheckIcon : WarningIcon}" width="30" alt="" />
		</div>
		<h2 class="approval-title">${i18n.t('Checklist for approval')}</h2>
		<div class="approval-progress">
			<span class="approval-bar"><span class="approval-bar-fill" style="width:${percent}%"></span></span>
			<span class="approval-progress-text">${done} ${i18n.t('of')} ${items.length} ${i18n.t('done')}</span>
		</div>
		<ul class="approval-list">
			${items
				.map(
					item => `
			<li class="approval-item ${item.status ? 'approval-item--ok' : 'approval-item--warning'}">
				<span class="approval-icon" aria-hidden="true">${item.status ? '\u2713' : ''}</span>
				<div class="approval-content">
					<h3 class="approval-item-title">${item.title}</h3>
					${item.text ? `<p class="approval-item-text">${item.text}</p>` : ''}
					${requirementLink(item.link, item.linkLabel)}
				</div>
			</li>`
				)
				.join('')}
		</ul>
	</div>`
}

/**
 * Ausgeschriebener Name eines Bundeslandes zum gespeicherten Enum-Wert.
 *
 * Bewusst ueber StateOptions statt ueber i18n: Die Namen stehen dort bereits,
 * und t('VIENNA') wuerde mangels Schluessel den Rohwert anzeigen.
 *
 * Nur fuer Stellen, an denen ein Bundesland **ausgewaehlt** wird (Auswahl-
 * felder, Filter, anklickbare Merkzettel). Angezeigt wird es sonst in der
 * Kurzform, siehe `stateLabel`.
 */
export const stateFullLabel = value => StateOptions.find(option => option.value === value)?.label || value || ''

/**
 * Bezeichnung eines Bundeslandes zur Anzeige — **immer die Kurzform**
 * (NÖ, OÖ, Stmk …, siehe `StateShortLabels`).
 *
 * Ausgeschrieben braucht „Niederösterreich" in jeder Liste zwei Zeilen und
 * drueckt die uebrigen Spalten zusammen. Die Kurzform gilt in allen Ansichten
 * und fuer alle Rollen; in Listen gehoert der volle Name zusaetzlich als
 * `cellToolTip` an die Spalte (erscheint beim Darueberfahren).
 */
export const stateLabel = value => StateShortLabels[value] || value || ''

/*
 * Der Ort einer Nachricht als eine Zeile.
 *
 * Seit 04.08.2026 wird die Anschrift strukturiert erfasst (Strasse, PLZ, Ort,
 * Land); davor stand sie als Freitext in `place`. Die lesenden Ansichten kannten
 * bis 08.08.2026 nur `place` — eine Veranstaltung, die mit den neuen Feldern
 * erfasst wurde, stand deshalb ohne jeden Ort da.
 *
 * Der Freitext hat Vorrang, wo er gepflegt ist: Er ist die Angabe, die jemand
 * bewusst so hingeschrieben hat ("Millennium City, Handelskai 94-96, 1200 Wien").
 */
export const newsLocation = item => {
	if (!item) return ''
	if (item.place) return item.place

	return [item.street, [item.zip, item.city].filter(Boolean).join(' '), item.country]
		.map(teil => (teil || '').trim())
		.filter(Boolean)
		.join(', ')
}

/**
 * Die Rolle eines Kontos in den Woertern der Oberflaeche — „Schueler:in",
 * „Lehrkraft", „Landesadmin", „Bundesadmin", „Unterstuetzer:in".
 *
 * Leer, wenn keine Rolle mitkommt: Bei einem geloeschten Konto steht im Verlauf
 * nur noch der mitgeschriebene Name, und ein Schild „—" waere dort keine
 * Auskunft, sondern eine Behauptung. Angezeigt wird sie ueber `UI/RoleChip`.
 */
export const roleLabel = value => RoleLabels[value] || ''

/*
 * Beschriftungen aus einer Optionsliste. Gleiche Begruendung wie bei
 * stateLabel: Die deutschen Namen stehen bereits in den Optionen, ein
 * i18n-Schluessel dafuer wäre eine zweite Stelle, die auseinanderlaufen kann.
 *
 * Faellt auf den Rohwert zurueck. Kommt vom Server ein Wert, den die
 * Oberflaeche noch nicht kennt, steht dort dann wenigstens etwas.
 */
const labelFrom = options => value => options.find(option => option.value === value)?.label || value || ''

// Eine Kette, ein Wort je Stufe — die Art spielt seit 22.08.2026 keine Rolle
// mehr.
export const ticketStatusLabel = labelFrom(TicketStatusOptions)
export const ticketTypeLabel = labelFrom(TicketTypeOptions)
export const ticketUrgencyLabel = labelFrom(TicketUrgencyOptions)
export const ticketImportanceLabel = labelFrom(TicketImportanceOptions)
export const changelogKindLabel = labelFrom(ChangelogKindOptions)
export const changelogEnvLabel = labelFrom(ChangelogEnvOptions)
export const changelogRepoLabel = labelFrom(ChangelogRepoOptions)

/*
 * Text, der in einer HTML-Zeichenkette landet — etwa im `html` eines
 * SweetAlert-Fensters. Namen und Firmenbezeichnungen kommen aus der
 * Selbstregistrierung; ohne diesen Schritt landet dort ungeprueft, was jemand
 * in sein Profil geschrieben hat.
 */
export const escapeHtml = value =>
	String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
